import { CommonModule } from '@angular/common';
import {
  Inject,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';

export interface DatadogConfig extends RumInitConfiguration {
  env: string;
  clientToken: string;
  applicationId: string;
  service: string;
}

export const DATADOG_CONFIG = new InjectionToken<DatadogConfig>(
  'DATADOG_CONFIG'
);

@NgModule({
  imports: [CommonModule],
})
export class DatadogModule {
  constructor(
    @Optional() @SkipSelf() parentModule: DatadogModule,
    @Inject(DATADOG_CONFIG) config: DatadogConfig
  ) {
    // Check if NgModule was previously loaded
    // https://angular.io/guide/ngmodule-faq#how-can-i-tell-if-an-ngmodule-or-service-was-previously-loaded
    if (parentModule) {
      throw new Error(
        'DatadogModule is already loaded. Import it in the AppModule only'
      );
    }

    if (!config) {
      throw new Error(
        'Provide config for DatadogModule using the forRoot method'
      );
    }

    if (location.protocol === 'https:') {
      // https://docs.datadoghq.com/real_user_monitoring/installation
      datadogRum.init({
        ...config,
        trackInteractions: true,
        allowedTracingOrigins: [
          /https:\/\/(?!apr\b).*\.(ygrene|ygreneworks)\.com/, // matches all api url formats except apr.*.ygreneworks.com (because APR lambda currently rejects datadog trace_id headers)
        ],
        service: `angular-${config.service}`, // required for use with `allowedTracingOrigins`
      });
    }
  }

  static forRoot(config: DatadogConfig): ModuleWithProviders<DatadogModule> {
    return {
      ngModule: DatadogModule,
      providers: [
        {
          provide: DATADOG_CONFIG,
          useValue: config,
        },
      ],
    };
  }
}
